// "Attach picture": the saved pictures from Settings › Knowledge, plus a straight-from-this-device
// upload. WHY the second one: the saved library is for the answers you send over and over (where the
// shut-off valve is). Troubleshooting is the opposite — the technician needs to send THIS hose, THIS
// error light, once, right now, and should not have to file it in Settings first.
// Picking or uploading hands back the asset id, its URL and a caption; the composer shows it as a chip.

import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, assetUrl, errorMessage, type KnowledgeItem } from '../api';
import { Dialog } from './Dialog';
import { Loading, ErrorBox } from './States';

export interface PickedPicture {
  asset_id: string;
  url: string;
  title: string;
  contentType?: string;
}

/** The picture behind a knowledge item, or null when the file is missing. */
export function pictureOf(item: KnowledgeItem): PickedPicture | null {
  const url = assetUrl(item.asset);
  if (!item.asset_id || !url) return null;
  return { asset_id: item.asset_id, url, title: item.title, contentType: item.asset?.content_type };
}

export function usePictures() {
  return useQuery({ queryKey: ['knowledge', 'picture'], queryFn: () => api.knowledge.list('picture'), staleTime: 30_000 });
}

export function PicturePickerDialog({ onPick, onClose }: { onPick: (p: PickedPicture) => void; onClose: () => void }) {
  const q = usePictures();
  const items = (q.data?.items ?? []).filter((i) => i.active !== false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function uploadAndAttach(file: File): Promise<void> {
    setErr(null);
    setBusy(true);
    try {
      const { asset } = await api.assets.upload(file);
      const url = assetUrl(asset);
      if (!url) throw new Error('The picture uploaded but has no address yet. Try again.');
      onPick({ asset_id: asset.id, url, title: asset.filename, contentType: asset.content_type });
      onClose();
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title="Attach a picture" onClose={onClose} wide>
      <div className="notice" style={{ marginBottom: 12 }}>
        <strong>Send a photo now</strong>
        <p className="small" style={{ margin: '4px 0 8px' }}>
          Take a picture or pick one from this device. It goes to the customer straight away and is
          not added to your saved answers.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          capture="environment"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) void uploadAndAttach(f);
          }}
        />
        <button className="btn primary" type="button" disabled={busy} onClick={() => fileRef.current?.click()}>
          {busy ? 'Uploading…' : 'Choose a photo'}
        </button>
        {err && <div className="danger-text small" style={{ marginTop: 6 }}>{err}</div>}
      </div>
      <p className="muted small">Or pick a saved one. These come from Settings › Knowledge › Pictures, and the caption is what the AI knows about each.</p>
      {q.isPending && <Loading />}
      {q.isError && <ErrorBox error={q.error} retry={() => q.refetch()} />}
      {q.isSuccess && items.length === 0 && (
        <div className="empty">
          <strong>No saved pictures yet</strong>
          <div>
            Use "Choose a photo" above to send one now, or add the ones you send often under{' '}
            <Link to="/settings/knowledge">Settings › Knowledge</Link>.
          </div>
        </div>
      )}
      {items.length > 0 && (
        <div className="picture-grid">
          {items.map((item) => {
            const p = pictureOf(item);
            return (
              <button
                key={item.id}
                type="button"
                className="picture-tile"
                disabled={!p}
                title={p ? item.title : 'The file is missing'}
                onClick={() => {
                  if (p) {
                    onPick(p);
                    onClose();
                  }
                }}
              >
                {p ? <img src={p.url} alt={item.title} loading="lazy" /> : <div className="picture-missing">missing file</div>}
                <div className="caption">{item.title}</div>
              </button>
            );
          })}
        </div>
      )}
      <div className="actions">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Dialog>
  );
}
